import axios from 'axios'
import { Platform } from 'react-native'
import { getLoginUrls } from '@/config/appConfig'
import { isProduction } from '@/utils/environment'

const getLoginUrl = (): string => {
  const loginUrls = getLoginUrls()
  return isProduction() ? loginUrls.production : loginUrls.development
}

const baseURL = getLoginUrl()
console.log('Login API Base URL:', baseURL)

const maskSensitiveData = (data: unknown) => {
  if (!data) {
    return data
  }
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data
    if (parsed && typeof parsed === 'object') {
      const clone = { ...(parsed as Record<string, unknown>) }
      if (typeof clone.password === 'string') {
        clone.password = '***'
      }
      return clone
    }
  } catch {
    // If it isn't JSON, just return as-is
  }
  return data
}

const loginApi = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 15000, // 15 second timeout
})

// Request interceptor to log outgoing requests and add platform header
loginApi.interceptors.request.use(
  (config) => {
    config.headers['X-Client-Type'] = Platform.OS === 'web' ? 'web' : 'mobile-app'
    console.log('Login API Request:', {
      method: config.method,
      url: config.url,
      baseURL: config.baseURL,
      fullUrl: `${config.baseURL}${config.url}`,
    })
    return config
  },
  (error) => {
    console.error('Login API Request Error:', error)
    return Promise.reject(error)
  },
)

// Response interceptor for detailed error logging
loginApi.interceptors.response.use(
  (response) => {
    console.log('Login API Success Response:', {
      status: response.status,
      statusText: response.statusText,
    })
    return response
  },
  (error) => {
    const errorInfo = {
      message: error.message,
      code: error.code,
      isTimeout: error.code === 'ECONNABORTED',
    }

    if (error.response) {
      // Server responded with error status
      console.error('Login API Error Response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers,
        url: error.response.config?.url,
      })
    } else if (error.request) {
      // Request made but no response
      console.error('Login API No Response:', {
        ...errorInfo,
        requestUrl: error.config?.url,
        requestMethod: error.config?.method,
        requestData: maskSensitiveData(error.config?.data),
      })

      // Check if this looks like a certificate error
      if (error.message?.includes('Trust anchor') || error.message?.includes('certificate')) {
        console.error(
          'CERTIFICATE ERROR DETECTED: Server may be redirecting HTTP to HTTPS or using invalid certificate',
        )
        console.error('Try using the IP address directly or verify server certificate')
      }
    } else {
      // Error in request setup
      console.error('Login API Error:', {
        ...errorInfo,
        config: error.config,
      })
    }
    return Promise.reject(error)
  },
)

export default loginApi
